/**
 * Bandeau de consentement MARKETING (Meta Pixel) — RGPD/ePrivacy.
 *
 * Umami (cookieless, first-party) est une mesure d'audience exemptée : il
 * tourne toujours et n'est PAS conditionné par ce bandeau. Seul le Meta Pixel
 * (cookies _fbp/_fbc, tracking publicitaire) exige un consentement préalable.
 *
 * Le runtime vit dans ``/_a/loader.js`` (multi-tenant par domaine, servi par
 * vendor_api) : il expose ``window.__vdAnalytics`` (pixel configuré ? consent
 * actuel ?) et ``window.vdMarketingConsent('granted'|'denied')``. Ce composant
 * n'affiche le bandeau QUE si un pixel est configuré pour le domaine ET que
 * l'utilisateur n'a pas encore choisi — donc invisible aujourd'hui sur les
 * déploiements sans pixel, et automatiquement actif pour wassal.tn /
 * tunisiaship quand leur pixel sera provisionné.
 */
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

interface VdAnalyticsRuntime {
  pixel?: boolean;
  consent?: string;
}

declare global {
  interface Window {
    __vdAnalytics?: VdAnalyticsRuntime;
    vdMarketingConsent?: (state: 'granted' | 'denied') => void;
  }
}

@Component({
  selector: 'vd-consent-banner',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  template: `
    @if (visible()) {
      <div class="vd-consent" role="dialog" aria-live="polite"
           [attr.aria-label]="'consent.title' | transloco">
        <div class="vd-consent-text">
          <strong>{{ 'consent.title' | transloco }}</strong>
          <span>{{ 'consent.body' | transloco }}</span>
        </div>
        <div class="vd-consent-actions">
          <button type="button" class="vd-consent-btn ghost" (click)="decide('denied')">
            {{ 'consent.decline' | transloco }}
          </button>
          <button type="button" class="vd-consent-btn primary" (click)="decide('granted')">
            {{ 'consent.accept' | transloco }}
          </button>
        </div>
      </div>
    }
  `,
  styles: [`
    .vd-consent {
      position: fixed;
      inset-inline: 12px;
      bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      z-index: 9800;
      display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px;
      padding: 14px 16px;
      border-radius: 14px;
      background: var(--vd-surface, #fff);
      color: var(--vd-text, #0f172a);
      box-shadow: 0 12px 40px -12px rgba(2, 32, 71, .35);
      border: 1px solid rgba(100, 116, 139, .18);
      max-width: 640px; margin-inline: auto;
      font-size: 13px; line-height: 1.45;
    }
    html[data-theme="dark"] .vd-consent {
      background: #101a2b; color: #e2e8f0;
      border-color: rgba(148, 163, 184, .18);
    }
    .vd-consent-text { display: flex; flex-direction: column; gap: 2px; flex: 1 1 260px; }
    .vd-consent-actions { display: flex; gap: 8px; margin-inline-start: auto; }
    .vd-consent-btn {
      border: none; border-radius: 10px; padding: 8px 16px;
      font: inherit; font-weight: 600; cursor: pointer;
    }
    .vd-consent-btn.primary { background: #1ca9e0; color: #fff; }
    .vd-consent-btn.ghost {
      background: transparent; color: inherit;
      border: 1px solid rgba(100, 116, 139, .35);
    }
  `],
})
export class ConsentBannerComponent implements OnInit, OnDestroy {
  readonly visible = signal(false);
  private timer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    // Le loader (/_a/loader.js, defer) peut s'exécuter après le boot Angular :
    // on sonde brièvement son état plutôt que de rater la fenêtre.
    let attempts = 0;
    this.timer = setInterval(() => {
      attempts += 1;
      const rt = window.__vdAnalytics;
      if (rt?.pixel && (rt.consent ?? 'unset') === 'unset') {
        this.visible.set(true);
        this.clearTimer();
      } else if ((rt && !rt.pixel) || attempts >= 10) {
        this.clearTimer();
      }
    }, 700);
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  decide(state: 'granted' | 'denied'): void {
    try {
      window.vdMarketingConsent?.(state);
    } catch {
      /* loader absent : le choix sera re-proposé au prochain chargement */
    }
    this.visible.set(false);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
